/**
 * logger.js - Simplified centralized logging utility for FuseSoul
 *
 * Central logging with levels, no console override.
 */

import { applySuppression } from './suppressBrowserWarnings';

// Default configuration
let loggerConfig = {
  enabled: process.env.NODE_ENV !== 'production', // Disabled in production by default
  level: 'info', // 'debug', 'info', 'warn', 'error'
  overrideConsole: false // not used anymore
};

// Log levels and their priorities
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

/**
 * Initialize the logger with custom configuration
 * @param {Object} config - Configuration options
 */
export const initLogger = (config = {}) => {
  // Update config
  loggerConfig = { ...loggerConfig, ...config };

  // Apply browser warning suppression when logging is disabled
  applySuppression({
    enabled: !loggerConfig.enabled,
    suppressManifestWarnings: true,
    suppressPWAWarnings: true,
    suppressPerformanceWarnings: true,
    suppressResourceWarnings: true
  });

  // Send configuration to service worker if available
  if (typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'LOGGER_CONFIG',
      config: loggerConfig
    });
  }

  return loggerConfig;
};

/**
 * The main logger object with methods for each log level
 */
const logger = {
  debug: (...args) => {
    const shouldLog =
      loggerConfig.enabled &&
      LOG_LEVELS[loggerConfig.level] <= LOG_LEVELS.debug;
    if (shouldLog) console.debug(...args);
  },

  info: (...args) => {
    const shouldLog =
      loggerConfig.enabled &&
      LOG_LEVELS[loggerConfig.level] <= LOG_LEVELS.info;
    if (shouldLog) console.log(...args);
  },

  warn: (...args) => {
    const shouldLog =
      loggerConfig.enabled &&
      LOG_LEVELS[loggerConfig.level] <= LOG_LEVELS.warn;
    if (shouldLog) console.warn(...args);
  },

  error: (...args) => {
    const shouldLog =
      loggerConfig.enabled &&
      LOG_LEVELS[loggerConfig.level] <= LOG_LEVELS.error;
    if (shouldLog) console.error(...args);
  },

  enable: () => {
    loggerConfig.enabled = true;
    logger.info('[Logger] Logging enabled');
  },

  disable: () => {
    logger.info('[Logger] Logging disabled');
    loggerConfig.enabled = false;
  },

  setLevel: (level) => {
    if (LOG_LEVELS[level] !== undefined) {
      loggerConfig.level = level;
      logger.info(`[Logger] Log level set to ${level}`);
    }
  },

  getConfig: () => ({ ...loggerConfig }),

  // Raw logging methods that bypass checks
  rawDebug: (...args) => console.debug(...args),
  rawInfo: (...args) => console.log(...args),
  rawWarn: (...args) => console.warn(...args),
  rawError: (...args) => console.error(...args),
};

export default logger;

// Expose a simple test helper in the browser
if (typeof window !== 'undefined') {
  window.fsLoggerTest = function (label = '') {
    const cfg = logger.getConfig();

    console.log('────────────────────────────');
    console.log('[fsLoggerTest] START', label || '(no label)');
    console.log('[fsLoggerTest] current logger config:', cfg);

    console.log('[fsLoggerTest] calling logger.debug(...)');
    logger.debug('[LOGGER DEBUG] If you see this, DEBUG logs are active.');

    console.log('[fsLoggerTest] calling logger.info(...)');
    logger.info('[LOGGER INFO] If you see this, INFO logs are active.');

    console.log('[fsLoggerTest] calling logger.warn(...)');
    logger.warn('[LOGGER WARN] If you see this, WARN logs are active.');

    console.log('[fsLoggerTest] calling logger.error(...)');
    logger.error('[LOGGER ERROR] If you see this, ERROR logs are active.');

    console.log('[fsLoggerTest] DONE', label || '(no label)');
    console.log('────────────────────────────');
  };
}
